import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useStudentContext from '@/data/hook/useStudentContext';
import { ListCourseProgressFilters } from '@/data/services/courseProgressServices';
import { simplifiedCourseProgressesQueryKeys } from '@/data/services/querykeys';
import { SimplifiedCourseProgress } from '@/models/StudentCourseProgress';
import Observable from '@/utils/observers/ObserverPattern';
import { useEffect, useState } from 'react';
import CourseProgressCarousel from './CourseProgressCarousel';
import { ProgressStatusEnum } from '@/enums/ProgressStatusEnum';

type CourseProgressCarouselFetcherProps = {
  section: ProgressStatusEnum;
  filters?: ListCourseProgressFilters;
  route?: string;
  studentId: number;
  invalidatesObserver?: Observable<number, SimplifiedCourseProgress>;
};

export default function CourseProgressCarouselFetcher({
  filters,
  invalidatesObserver,
  studentId,
  ...courseProgressCarouselProps
}: CourseProgressCarouselFetcherProps) {
  const { updateProgressObserver } = useStudentContext();
  const [carouselFilters, setCarouselFilters] =
    useState<ListCourseProgressFilters>({});
  const allFilters: ListCourseProgressFilters = {
    ...filters,
    ...carouselFilters,
    status: courseProgressCarouselProps.section,
  };

  const { queryFn, queryKey } = simplifiedCourseProgressesQueryKeys.list(
    studentId,
    allFilters,
  )._ctx.infinity;

  const {
    results: courseProgresses,
    isInitialLoading,
    isFetchingNextPage: fetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    cacheManager,
  } = useInfiniteService({
    queryFn,
    queryKey,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const onFilter = (value: ListCourseProgressFilters) => {
    setCarouselFilters(prev => ({ ...prev, ...value }));
  };

  useEffect(
    () =>
      updateProgressObserver.subscribe({
        id: NaN,
        action: courseProgress => {
          courseProgress && cacheManager.update(courseProgress);
        },
      }),
    [updateProgressObserver, cacheManager],
  );

  return (
    <CourseProgressCarousel
      {...courseProgressCarouselProps}
      error={error}
      filters={allFilters}
      onFilter={onFilter}
      courseProgresses={courseProgresses}
      onMutate={courseProgress =>
        invalidatesObserver?.notifyAll(1, courseProgress)
      }
      fetchingNextPage={fetchingNextPage}
      hasNextPage={hasNextPage}
      isLoading={isInitialLoading}
      onReachEnd={fetchNextPage}
    />
  );
}
